import { useCallback, useRef, useState } from "react";
import dayjs from "dayjs";
import { defaultSeriesDateTo, validateSeriesRange } from "../../reservas/components/courtSeriesModel";
import { DATE_FORMAT } from "./agendaLayout";

/* Estado del bloque "Turno fijo" que comparten Turnos Beauty y Canchas: intervalo, fecha final y vista previa de fechas.
   Cada vertical aporta lo suyo: `claveParts` (los datos que invalidan la vista previa) y `pedirVistaPrevia`, que llama a su
   endpoint y devuelve `{ dates: [{ date, available, reason }], availableCount? }`. La vista previa vale solo para los datos
   exactos con los que se pidió: si cambia cualquiera (incluidos intervalo y fin) la clave deja de coincidir. */
export default function useSerieFija({ date, listo, claveParts, pedirVistaPrevia, mensajeDeError }) {
  const [fixed, setFixed] = useState(false);
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [preview, setPreview] = useState(null); // { key, loading, error, dates, availableCount }
  const [skipped, setSkipped] = useState(null); // { key, dates } from a 409 on create
  const pedidoRef = useRef(0);

  const dateTo = customDateTo || defaultSeriesDateTo(date);
  const rangeError = fixed ? validateSeriesRange(date, dateTo) : null;
  const listoFijo = Boolean(fixed && listo);
  const key = listoFijo ? [date, dateTo, intervalWeeks, ...claveParts].join("|") : null;

  const current = key && preview && preview.key === key && !preview.loading && !preview.error ? preview : null;
  const availableCount = current ? current.availableCount : 0;
  const skippedCurrent = key && skipped && skipped.key === key ? skipped.dates : null;

  const verFechas = useCallback(async () => {
    if (!key || rangeError) return;
    const pedido = ++pedidoRef.current;
    setSkipped(null);
    setPreview({ key, loading: true, error: "", dates: [], availableCount: 0 });
    try {
      const { dates, availableCount: count } = await pedirVistaPrevia({ intervalWeeks, dateTo });
      if (pedido !== pedidoRef.current) return;
      const total = Number.isFinite(count) ? count : dates.filter((d) => d.available).length;
      setPreview({ key, loading: false, error: "", dates, availableCount: total });
    } catch (e) {
      if (pedido !== pedidoRef.current) return;
      setPreview({ key, loading: false, error: mensajeDeError(e), dates: [], availableCount: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, rangeError, intervalWeeks, dateTo]);

  const alternar = (value) => {
    setFixed(value);
    setSkipped(null);
  };
  const cambiarFin = (d) => {
    if (d && dayjs(d).isValid()) setCustomDateTo(dayjs(d).format(DATE_FORMAT));
  };
  const reiniciar = useCallback(() => {
    setFixed(false);
    setIntervalWeeks(1);
    setCustomDateTo(null);
    setPreview(null);
    setSkipped(null);
  }, []);

  return {
    fixed,
    alternar,
    intervalWeeks,
    setIntervalWeeks,
    dateTo,
    cambiarFin,
    rangeError,
    listo: listoFijo,
    key,
    preview,
    current,
    availableCount,
    skipped: skippedCurrent,
    setSkipped: (dates) => setSkipped(key ? { key, dates } : null),
    verFechas,
    reiniciar,
  };
}
